<script setup lang="ts">
import CONSTANTS from '@/helpers/constants';
import { isProfileCompleted as checkProfileCompleted } from '@/helpers/profileHelpers';
import { injectGoftinoScript } from '@/helpers/utils';
import { zeblineSetUserAttribute, zeblineUpdateUserProfile } from '@/helpers/zebline';
import { Profile } from '@/pages/Profile/Profile.types';
import { initializeGA, initializeYandex, initializeZebline } from '@/services/analytics';
import { useAppStore } from '@/stores/app';
import { useToastsStore } from '@/stores/toasts';
import { AccentColors, Layouts } from '@/types/enums';
import { defineAsyncComponent } from 'vue';
import { useRecaptchaProvider } from 'vue-recaptcha';
import 'vue-select/dist/vue-select.css';

const Panel = defineAsyncComponent(() => import('@/layouts/Panel/Panel.vue'));
const Verify = defineAsyncComponent(() => import('@/layouts/Verify/Verify.vue'));
const WelcomeModal = defineAsyncComponent(() => import('@/components/Modals/WelcomeModal/WelcomeModal.vue'));
const AppLoadingModal = defineAsyncComponent(() => import('@/components/Modals/AppLoadingModal/AppLoadingModal.vue'));
const AskMe = defineAsyncComponent(() => import('@/components/AskMe/AskMe.vue'));
const NewestNotification = defineAsyncComponent(
  () => import('@/components/Modals/NewestNotification/NewestNotification.vue'),
);

useRecaptchaProvider();

const router = useRouter();
const { t } = useI18n();
const toastStore = useToastsStore();
const appStore = useAppStore();
const goftinoElement = ref();

const currentRouteLayout = computed(() => router.currentRoute.value.meta.layout);

const isAppLoadingModalOpen = computed(() => {
  if (currentRouteLayout.value === Layouts.Panel) {
    return appStore.isLoading;
  }

  return false;
});

const handleSetDirAndLocal = () => {
  const html = document.querySelector('html') as HTMLElement;

  /* if (locale.value === 'fa') {
html.setAttribute('lang', 'fa')
} else if (locale.value === 'ar') {
html.setAttribute('lang', 'ar')
} */

  html.setAttribute('lang', 'fa');
  html.setAttribute('dir', 'rtl');
};

onMounted(async () => {
  // Update cookie panel-version upon load
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  document.cookie = `panel-version=3; expires=${expires.toUTCString()}; path=/`;

  // handle set dir and local
  handleSetDirAndLocal();

  // reset all data
  appStore.resetAllData();

  handleChangeToPortrait();

  screen.orientation.addEventListener('change', () => {
    handleChangeToPortrait();
  });

  window.addEventListener('goftino_ready', () => {
    const goftinoIframe = document.querySelector('#goftino_w') as HTMLElement;
    goftinoElement.value = goftinoIframe;
  });

  window.addEventListener('goftino_closeWidget', () => {
    if (currentRouteLayout.value === Layouts.Auth) return;
  });

  initBasedOnLayout(currentRouteLayout.value as Layouts);
});

const handleChangeToPortrait = () => {
  if (
    screen &&
    screen.orientation &&
    screen.orientation.type === 'landscape-primary' &&
    window.innerHeight <= 600 &&
    window.innerWidth <= CONSTANTS.BREAKPOINTS.MD
  ) {
    toastStore.show({
      accent: AccentColors.Warning,
      text: t('general.rotate-to-portrait'),
      timeout: 20000,
    });
  }
};

watch(
  () => appStore.profile,
  async (profile) => {
    if (!profile) return;

    if (!goftinoElement.value) await until(goftinoElement).changed();
    Goftino.setUser({
      email: profile.email,
      name: profile.nickName,
      about: `username: ${profile.userName}`,
      phone: profile.mobileNumber,
      forceUpdate: true,
    });

    Goftino.setWidget({
      counter: '.goftino-counter-indicator',
    });
  },
);

const initBasedOnLayout = async (layout: Layouts) => {
  // Initialize analytics
  initializeZebline();
  initializeGA();
  initializeYandex();

  if (layout === Layouts.Panel) {
    if (import.meta.env.VITE_INJECT_GOFTINO) injectGoftinoScript();

    // Get configs for logged in user
    await appStore.getAllData();
    if (appStore.profile) {
      const isProfileCompleted = checkProfileCompleted({
        profile: appStore.profile as Profile,
        profileExtraInformationStatus: appStore.profileExtraInformationStatus,
      });

      zeblineUpdateUserProfile(appStore.profile);
      zeblineSetUserAttribute<'profile_completed'>('profile_completed', isProfileCompleted);
    }
  } else if (layout === Layouts.Auth) {
    // Reset all data
    appStore.resetAllData();
  }

  if (!goftinoElement.value) await until(goftinoElement).changed();
  if (layout === Layouts.Panel) {
    Goftino.close();
  }
};

watch(
  () => appStore.userACL,
  () => {
    if (appStore.userACL && appStore.canRead('profile')) {
      appStore.getProfile();
    }
  },
);

watch(
  currentRouteLayout,
  async (layout) => {
    initBasedOnLayout(layout as Layouts);
  },
  { immediate: true },
);
</script>

<template>
  <transition mode="out-in" name="fade">
    <!-- Panel layout -->
    <Panel v-if="currentRouteLayout === Layouts.Panel">
      <RouterView v-slot="{ Component, route }">
        <transition mode="out-in" name="page">
          <section v-if="route.path" :key="route.path">
            <component :is="Component" />
          </section>
        </transition>
      </RouterView>
    </Panel>
    <!-- Auth layout -->
    <Auth v-else-if="currentRouteLayout === Layouts.Auth">
      <RouterView v-slot="{ Component, route }">
        <transition mode="out-in" name="fade">
          <section v-if="route.path" :key="route.path">
            <component :is="Component" />
          </section>
        </transition>
      </RouterView>
    </Auth>
    <!-- Verify layout -->
    <Verify v-else-if="currentRouteLayout === Layouts.Verify">
      <RouterView v-slot="{ Component, route }">
        <transition mode="out-in" name="fade">
          <section v-if="route.path" :key="route.path">
            <component :is="Component" />
          </section>
        </transition>
      </RouterView>
    </Verify>
  </transition>

  <!-- App initial loading modal -->
  <AppLoadingModal :isOpen="isAppLoadingModalOpen" />

  <!-- New User Welcome modal -->
  <WelcomeToOrganizer />
  <WelcomeModal :isOpen="appStore.isNewUserWelcomeModalOpen" @close="appStore.isNewUserWelcomeModalOpen = false" />

  <KeepAlive>
    <NewestNotification
      v-if="!isAppLoadingModalOpen && currentRouteLayout === Layouts.Panel"
      :key="'newest-notification'"
    />
  </KeepAlive>

  <!-- Initialize toasts -->
  <Toasts />

  <!-- Ask me -->
  <AskMe v-if="currentRouteLayout === Layouts.Auth" />
</template>
